import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { GlobalSettings } from '../shared/domain/configuration.model';
import { IState } from '../../shared/models/state';
import { Store } from '@ngrx/store';
import { getGlobalSettingsSelector } from './global-settings.reducer';
import { UpdateGlobalSettingsAction } from './global-settings.actions';
import { ProjectsService } from '../projects/projects.service';
import { ToastrService } from 'ngx-toastr';
import { isEmptyOrValidPhoneNumber } from '../../shared/utils/validate-number';
import { VerifyPhoneNumberService } from '../../shared/verify-phone-number/verify-phone-number.service';
import { TranslateService } from '@ngx-translate/core';
import { GlobalSettingsFormValidator } from './ip-exclusion/GlobalSettingsFormValidator';

@Component({
  selector: 'cc-global-settings',
  templateUrl: './global-settings.component.html',
  styleUrls: ['./global-settings.component.scss']
})
export class GlobalSettingsComponent implements OnInit {
  public settingsForm: FormGroup;
  public globalSettings: GlobalSettings;
  public projectsExists: boolean;
  public isPhoneNumberVerificationSent = false;
  public phoneNumberVerificationCode: string;
  public verifiedPhoneNumber: string;
  public isVerifiedPhoneNumber = true;
  public minValue = 0;
  public maxValue = 999;

  constructor(
    private projectsService: ProjectsService,
    private fb: FormBuilder,
    private store: Store<IState>,
    private toastr: ToastrService,
    private verifyPhoneNumberService: VerifyPhoneNumberService,
    public translate: TranslateService
  ) {}

  ngOnInit() {
    this.projectsService
      .allProjects()
      .map((projects: any[]) => {
        return projects.length > 0;
      })
      .subscribe(projectsExists => (this.projectsExists = projectsExists));

    this.store.select(getGlobalSettingsSelector).subscribe(data => {
      this.globalSettings = data;
    });

    this.initForm();
    this.verifiedPhoneNumber = this.globalSettings.newLeadPhoneNumber;
  }

  get isDailySummaryEnabled() {
    return this.settingsForm.get('dailySummaryEnabled').value;
  }

  get isNewLeadEmailEnabled() {
    return this.settingsForm.get('newLeadEmailEnabled').value;
  }

  get isNewLeadSmsEnabled() {
    return this.settingsForm.get('newLeadSmsEnabled').value;
  }

  get newLeadPhoneNumber() {
    return this.settingsForm.get('newLeadPhoneNumber').value;
  }

  get isGoogleAnalyticsIntegrationEnabled() {
    return this.settingsForm.get('googleAnalyticsEnabled').value;
  }

  private initForm() {
    this.settingsForm = this.fb.group(
      {
        popupDisplayLimit: [
          this.globalSettings.popupDisplayLimit,
          [Validators.min(this.minValue), Validators.max(this.maxValue)]
        ],
        dailySummaryEnabled: [this.globalSettings.dailySummaryEnabled],
        dailySummaryEmail: [this.globalSettings.dailySummaryEmail, Validators.email],
        dailySummaryTimeZone: [this.globalSettings.dailySummaryTimeZone],
        newLeadEmailEnabled: [this.globalSettings.newLeadEmailEnabled],
        newLeadEmail: [this.globalSettings.newLeadEmail, [Validators.email]],
        newLeadSmsEnabled: [this.globalSettings.newLeadSmsEnabled],
        newLeadPhoneNumber: [this.globalSettings.newLeadPhoneNumber, this.validatePhoneNumber],
        googleAnalyticsEnabled: [this.globalSettings.googleAnalyticsEnabled],
        googleAnalyticsUaTrackerId: [this.globalSettings.googleAnalyticsUaTrackerId, Validators.pattern(/^UA-\d+-\d+$/)]
      },

      { validator: GlobalSettingsFormValidator }
    );
  }

  get isFormValid() {
    return this.settingsForm.valid;
  }

  validatePhoneNumber(control: FormControl) {
    return control.parent && isEmptyOrValidPhoneNumber(control.parent.get('newLeadPhoneNumber').value)
      ? null
      : { phoneNumber: true };
  }

  get isValidPhoneNumber() {
    return this.newLeadPhoneNumber && this.settingsForm.get('newLeadPhoneNumber').valid;
  }

  onPhoneNumberChange() {
    this.isVerifiedPhoneNumber = this.newLeadPhoneNumber === this.verifiedPhoneNumber;
  }

  get isValidPhoneNumberVerify() {
    return !this.isNewLeadSmsEnabled || (this.isNewLeadSmsEnabled && this.isVerifiedPhoneNumber);
  }

  onPhoneNumberVerify() {
    this.verifyPhoneNumberService.sendCode(this.newLeadPhoneNumber).subscribe(() => {
      this.isPhoneNumberVerificationSent = true;
    });
  }

  onVerificationCodeChange(verificationCode: string) {
    this.phoneNumberVerificationCode = verificationCode;
  }

  onPhoneNumberVerificationCodeVerify() {
    this.verifyPhoneNumberService
      .verifyCode(this.newLeadPhoneNumber, this.phoneNumberVerificationCode)
      .subscribe(() => {
        this.translate.get('LEAD_GENERATION.TOAST.PHONE_NUMBER_VERIFIED').subscribe((res: string) => {
          this.toastr.success(res);
        });
        this.isVerifiedPhoneNumber = true;
        this.isPhoneNumberVerificationSent = false;
        this.verifiedPhoneNumber = this.newLeadPhoneNumber;
      });
  }

  onSubmit() {
    // when form is not valid the scrollToFirstInvalidField directive will be used
    if (this.isFormValid && this.isValidPhoneNumberVerify) {
      const formSettings = this.settingsForm.getRawValue();
      const updatedGlobalSettings = { ...this.globalSettings, ...formSettings };
      this.store.dispatch(new UpdateGlobalSettingsAction(updatedGlobalSettings));
      this.translate.get('LEAD_GENERATION.TOAST.GLOBAL_SETTINGS_UPDATED').subscribe((res: string) => {
        this.toastr.success(res);
      });
    }
  }
}
